import { Icon } from '@/components/icon/Icon';
import { MyOrderAction, ParticipantDataModalProps, SelectedOrderProps, WatchListDataProps } from './interfaces';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Enums } from '@tralent/database';
import {
	EDIT_DELETE,
	OPEN_AT,
	CREATED_AT,
	CLOSED_AT,
	CLOSED_MODE,
	OPEN_PRICE,
	CURRENT_PRICE,
	CLOSED_PRICE,
	PENDING_OPEN_PRICE,
	PROFIT_LOSS,
	QUANTIY,
	SL_PRICE,
	TICKER_LABEL,
	TP_PRICE,
	TYPE,
	SELECT_SYMBOL,
} from '../_features/orderColumns';
import ValueColor from '@/components/valueColor/ValueColor';
import { profitColorSwitch, resampleFreqList } from '@/lib/_utils';
import { Translate } from 'next-translate';

interface UserOrdersListDataColumnsProps {
	t: Translate;
	callback?: ({ id, action }: MyOrderAction) => void;
	status?: Enums<'order_statuses'>;
}

/* ARENA - INITIAL VALUES */
export const tournamentChannel = 'tournament-channel';
export const symbolsChannel = 'symbols';
export const ordersChannel = 'orders-channel';
export const symbolsTable = 'symbols';
export const ordersTable = 'orders';
export const tournamentsTable = 'tournaments';
export const tournamentUserTable = 'tournament_user';

export const initialParticipantDataModalValues: ParticipantDataModalProps = {
	open: false,
	username: undefined,
	display_name: undefined,
	data: [],
	status: 'open',
};
export const initialTicketHistoryDataValues = [{ name: '', data: [] }];
export const initialSelectedWatchListSymbol: WatchListDataProps = {
	id: 0,
	bid: 0,
	ask: 0,
	ticker: '',
	label: '',
	ts: '',
	decimal_precision: 5,
	display_name: '',
};
export const rankingInitialValues = {
	data: [],
	totalCount: 0,
};
export const orderMaskInitialValues: SelectedOrderProps = {
	id: 0,
	open_mode: 'manual',
	pending_open_price: null,
	quantity: 1,
	type: 'buy',
	sl_price: null,
	tp_price: null,
	tournament_id: 0,
	action: 'edit',
	bid: 0,
	ask: 0,
	decimal_precision: 0,
	closed_at: null,
	closed_mode: null,
	closed_price: null,
	created_at: '',
	current_price: null,
	open_at: null,
	open_price: null,
	order_chan: null,
	pl: null,
	status: null,
	ticker: '',
	ticker_decimal_precision: null,
	ticker_label: null,
	updated_at: '',
	username: '',
	duration: null,
};

export const rankingPreviewInitialValues = {
	data: [],
	lastRankUpdateData: null,
};

type Trend = 'up' | 'down' | 'stable';
export interface WatchlistRow {
	label: string;
	bid: number;
	ask: number;
	bid_trend?: Trend;
	ask_trend?: Trend;
	decimal_precision?: number;
}

/* COLUMNS */
const columnHelper = createColumnHelper();
const columnHelperWatchList = createColumnHelper<WatchlistRow>();

export function watchListDataColumns(): ColumnDef<WatchlistRow, any>[] {
	return [
		columnHelperWatchList.accessor('label', {
			id: '1-watch-list-symbol',
			header: 'Symbol',
			cell: (data) => <div className='text-sm'>{data.getValue()}</div>,
		}),
		columnHelperWatchList.accessor('bid', {
			id: '2-watch-list-bid',
			header: 'Bid',
			cell: (data) => {
				const value = data.getValue() as number;
				const trend = data?.row?.original?.bid_trend;
				const decimal_precision = data?.row?.original?.decimal_precision || 0;
				return (
					<div className='text-sm'>
						<ValueColor
							variant={(() => {
								if (trend) {
									if (trend === 'stable') return 'white';
									if (trend === 'up') return 'success';
									if (trend === 'down') return 'danger';
								} else return 'white';
							})()}>
							{value.toFixed(decimal_precision)}
						</ValueColor>
					</div>
				);
			},
		}),
		columnHelperWatchList.accessor('ask', {
			id: '3-watch-list-ask',
			header: 'Ask',
			cell: (data) => {
				const value = data.getValue() as number;
				const trend = data?.row?.original?.ask_trend;
				const decimal_precision = data?.row?.original?.decimal_precision || 0;
				return (
					<div className='text-sm'>
						<ValueColor
							variant={(() => {
								if (trend) {
									if (trend === 'stable') return 'white';
									if (trend === 'up') return 'success';
									if (trend === 'down') return 'danger';
								} else return 'white';
							})()}>
							{value.toFixed(decimal_precision)}
						</ValueColor>
					</div>
				);
			},
		}),
	];
}

export function userOrdersListDataColumns({ t, callback, status }: UserOrdersListDataColumnsProps) {
	const openColumns: ColumnDef<unknown, never>[] = [
		OPEN_AT(t),
		TYPE(t),
		TICKER_LABEL(t),
		CURRENT_PRICE(t),
		OPEN_PRICE(t),
		QUANTIY(t),
		SL_PRICE(t),
		TP_PRICE(t),
		PROFIT_LOSS(t),
	];

	const pendingColumns = [
		CREATED_AT(t),
		TYPE(t),
		TICKER_LABEL(t),
		CURRENT_PRICE(t),
		PENDING_OPEN_PRICE(t),
		QUANTIY(t),
		SL_PRICE(t),
		TP_PRICE(t),
	];

	const closedColumns: ColumnDef<unknown, never>[] = [
		OPEN_AT(t),
		CLOSED_AT(t),
		TYPE(t),
		TICKER_LABEL(t),
		OPEN_PRICE(t),
		CLOSED_PRICE(t),
		QUANTIY(t),
		SL_PRICE(t),
		TP_PRICE(t),
		CLOSED_MODE(t),
		PROFIT_LOSS(t),
	];

	if (callback) {
		openColumns.unshift(SELECT_SYMBOL(t, callback));
		pendingColumns.unshift(SELECT_SYMBOL(t, callback));
		closedColumns.unshift(SELECT_SYMBOL(t, callback));

		openColumns.push(EDIT_DELETE(callback));
		pendingColumns.push(EDIT_DELETE(callback));
	}

	let columns: ColumnDef<unknown, never>[] = [];

	if (status === 'open') {
		columns = openColumns;
	} else if (status === 'pending') {
		columns = pendingColumns;
	} else if (status === 'closed') {
		columns = closedColumns;
	}

	return columns;
}

export function participantOrdersListDataColumns({ t, status }: { t: Translate; status?: Enums<'order_statuses'> }) {
	const openColumns: ColumnDef<unknown, never>[] = [
		OPEN_AT(t),
		TYPE(t),
		TICKER_LABEL(t),
		OPEN_PRICE(t),
		QUANTIY(t),
		SL_PRICE(t),
		TP_PRICE(t),
		PROFIT_LOSS(t),
	];

	const closedColumns: ColumnDef<unknown, never>[] = [
		OPEN_AT(t),
		CLOSED_AT(t),
		TYPE(t),
		TICKER_LABEL(t),
		OPEN_PRICE(t),
		CLOSED_PRICE(t),
		QUANTIY(t),
		CLOSED_MODE(t),
		PROFIT_LOSS(t),
	];

	let columns: ColumnDef<unknown, never>[] = [];

	if (status === 'open') {
		columns = openColumns;
	} else if (status === 'closed') {
		columns = closedColumns;
	}

	return columns;
}

type AmountFormatter = (value: number) => string;

export function rankingDataColumns(tournamentIsFree: boolean, amountWithUserCurrency: AmountFormatter) {
	return [
		columnHelper.accessor('rank', {
			id: '1-#-rank',
			header: '#',
			size: 65,
			cell: (data) => <div className='text-sm'>{data.getValue()}</div>,
		}),
		columnHelper.accessor('username', {
			id: '2-username-rank',
			header: 'USERNAME',
			cell: (data) => {
				const row = data.row?.original as { display_name?: string | null; username?: string | null };
				const displayName = row?.display_name ?? data.getValue();
				return <div className={`text-sm`}>{displayName}</div>;
			},
		}),
		columnHelper.accessor('pl', {
			id: '3-pl-rank',
			header: 'PROFIT',
			cell: ({ getValue }) => {
				const value = Number(getValue())?.toFixed(2);
				const profitColor = profitColorSwitch(getValue());
				const formattedValue = Number(value) > 0 ? `+${value}` : `${value}`;

				return (
					<div className='flex text-sm'>
						{getValue() != null ? <ValueColor variant={profitColor}>{formattedValue}%</ValueColor> : '-'}
					</div>
				);
			},
		}),
		...(!tournamentIsFree
			? [
					columnHelper.accessor('reward', {
						id: '4-reward-rank',
						header: 'REWARD',
						cell: ({ getValue }) => (
							<div className='text-sm'>{getValue() != null ? amountWithUserCurrency(getValue()) : '-'}</div>
						),
					}),
				]
			: []),
	];
}

/* TOGGLES */
export interface OrderToggleOptionModel {
	id: string;
	label: string;
	value: {
		type: string;
	};
}
export const createOrderToggleOptions: OrderToggleOptionModel[] = [
	{
		id: 'manual',
		label: 'Now',
		value: { type: 'manual' },
	},
	{
		id: 'pending',
		label: 'Pending',
		value: { type: 'pending' },
	},
];

export const chartToggleOptions = [
	{
		id: 'area',
		label: <Icon name='lineChartIcon' size='md' variant='white' />,
		value: { type: 'area' },
	},
	{
		id: 'candlestick',
		label: <Icon name='candlestickChartIcon' size='md' variant='white' />,
		value: { type: 'candlestick' },
	},
];

export const resampleFreqOptions = resampleFreqList.map((resampleFreq) => {
	return {
		id: resampleFreq.val.value,
		label: resampleFreq.val.value,
		value: resampleFreq.val.value,
	};
});

export const myOrdersToggleOptions = [
	{
		id: 'open',
		label: 'Active',
		value: { type: 'open' },
	},
	{
		id: 'pending',
		label: 'Pending',
		value: { type: 'pending' },
	},
	{
		id: 'closed',
		label: 'Closed',
		value: { type: 'closed' },
	},
];

export const rankingUserOrderToggle = [
	{
		id: 'open',
		label: 'Active',
		value: { type: 'open' },
	},
	{
		id: 'closed',
		label: 'Closed',
		value: { type: 'closed' },
	},
];

/* FORMAT FUNCTIONS */
export function formatOrderMode(type: string, open_mode: Enums<'order_open_modes'>) {
	let label = type;
	if (!!open_mode && open_mode != 'manual') {
		label = label + ' ' + open_mode;
	}
	return label.toUpperCase();
}
